/**
 * 来場者QRスキャン画面
 * 企画・屋台のQRを判別し、ポイント取得または屋台評価へ進みます
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Platform, ScrollView, Text, TextInput, View } from '../../../shared/components/WebPrimitives';
import { useFocusEffect, useIsFocused } from '../../../navigation/webNavigation';
import useCameraPermissions from '../../../shared/hooks/useCameraPermissions';
import QrScanner from '../../../shared/components/QrScanner';
import {
  POINT_CLAIM_BUTTON_LABEL,
  POINT_CLAIM_BUTTON_LOADING_LABEL,
  VISITOR_SCAN_CAMERA_ENABLE_BUTTON_LABEL,
  VISITOR_SCAN_CAMERA_POINT_CLAIM_SUCCESS_TITLE,
  VISITOR_SCAN_CAMERA_PERMISSION_REQUIRED_MESSAGE,
  VISITOR_SCAN_CAMERA_SCANNED_GUIDE_MESSAGE,
  VISITOR_SCAN_CAMERA_SCANNED_MESSAGE,
  VISITOR_SCAN_CAMERA_SECTION_TITLE,
  VISITOR_POINT_CLAIM_EVENT_UNAVAILABLE_ERROR_MESSAGE,
  VISITOR_POINT_CLAIM_FAILED_ERROR_MESSAGE,
  VISITOR_POINT_CLAIM_INVALID_INPUT_ERROR_MESSAGE,
  VISITOR_POINT_CLAIM_LIMIT_EXCEEDED_ERROR_MESSAGE,
  VISITOR_POINT_CLAIM_RATE_LIMITED_ERROR_MESSAGE,
  VISITOR_POINT_CLAIM_SESSION_ERROR_MESSAGE,
  VISITOR_POINT_CLAIM_TOKEN_EXHAUSTED_ERROR_MESSAGE,
  VISITOR_POINT_CLAIM_TOKEN_EXPIRED_ERROR_MESSAGE,
  VISITOR_POINT_CLAIM_TOKEN_INACTIVE_ERROR_MESSAGE,
  VISITOR_POINT_CLAIM_TOKEN_NOT_FOUND_ERROR_MESSAGE,
  VISITOR_SURVEY_CLAIM_ALREADY_CLAIMED_ERROR_MESSAGE,
  VISITOR_SURVEY_CLAIM_FAILED_ERROR_MESSAGE,
  VISITOR_SURVEY_CLAIM_RATE_LIMITED_ERROR_MESSAGE,
  VISITOR_OFFLINE_SUBMIT_BLOCKED_MESSAGE,
} from '../constants';
import NoticeToast from '../components/NoticeToast';
import VisitorBottomSwitchBar from '../components/VisitorBottomSwitchBar';
import { useIsGuidedTourRunning } from '../components/VisitorGuidedTour';
import VisitorHomeBackButton from '../components/VisitorHomeBackButton';
import StallRatingRewardClaimPanel from '../components/StallRatingRewardClaimPanel';
import useStallRatingRewardClaim from '../hooks/useStallRatingRewardClaim';
import {
  visitorPointsClaim,
  VISITOR_POINTS_CLAIM_EVENT_UNAVAILABLE_CODE,
  VISITOR_POINTS_CLAIM_LIMIT_EXCEEDED_CODE,
  VISITOR_POINTS_CLAIM_RATE_LIMITED_CODE,
  VISITOR_POINTS_CLAIM_RULE_NOT_FOUND_CODE,
  VISITOR_POINTS_CLAIM_SESSION_INVALID_CODE,
  VISITOR_POINTS_CLAIM_TOKEN_EXHAUSTED_CODE,
  VISITOR_POINTS_CLAIM_TOKEN_EXPIRED_CODE,
  VISITOR_POINTS_CLAIM_TOKEN_INACTIVE_CODE,
  VISITOR_POINTS_CLAIM_TOKEN_NOT_FOUND_CODE,
  VISITOR_POINTS_CLAIM_TOKEN_UNAVAILABLE_CODE,
} from '../services/visitorPointService';
import {
  visitorSurveyClaimPoints,
  VISITOR_SURVEY_CLAIM_ALREADY_CLAIMED_CODE,
  VISITOR_SURVEY_CLAIM_FAILED_CODE,
  VISITOR_SURVEY_CLAIM_NOT_FOUND_CODE,
  VISITOR_SURVEY_CLAIM_RATE_LIMITED_CODE,
  VISITOR_SURVEY_CLAIM_SESSION_INVALID_CODE,
} from '../services/visitorSurveyService';
import useVisitorSession from '../hooks/useVisitorSession';
import { queuePendingStampCelebration } from '../services/visitorStampCelebrationService';
import { upsertLocalEventVisit } from '../services/visitorStampCacheService';
import { VISITOR_ROUTES } from '../../../navigation/routeNames';
import AppButton from '../../../shared/components/AppButton';
import ScreenLayout from '../../../shared/components/ScreenLayout';
import { tourAnchor } from '../../../shared/components/guidedTourAnchor';
import { INPUT_PLACEHOLDER_COLOR } from '../../../shared/constants/accessibility';
import usePublicAppSettings from '../../../shared/hooks/usePublicAppSettings';
import useNetworkStatus from '../../../shared/hooks/useNetworkStatus';
import { AppError } from '../../../shared/utils/errorHandling';
import { logError, logInfo } from '../../../shared/utils/logger';
import { playVisitorSound, VISITOR_SOUND_NAMES } from '../../../shared/utils/visitorAudio';
import {
  extractPointClaimToken,
  isPointClaimManualCode,
  isPointClaimQrPayload,
} from '../../../shared/utils/pointClaimQr';
import {
  hasClaimedPointToken,
  isRepeatablePointClaimToken,
  rememberClaimedPointToken,
} from '../utils/claimedPointTokenMemory';
import {
  extractStallRatingAccessToken,
  extractStallRatingAccessTokenFromInput,
} from '../../../shared/utils/stallRatingQr';

/** QR手入力欄のエラー説明ID */
const SCAN_INPUT_ERROR_ID = 'visitor-scan-input-error';

/**
 * エラーコードに対応する表示文言を返す
 * @param {string} errorCode - エラーコード
 * @param {{ supportContactLabel: string }} settings - 公開アプリ設定
 * @returns {string} 表示文言
 */
const mapPointClaimErrorMessage = (errorCode, settings) => {
  if (errorCode === VISITOR_POINTS_CLAIM_TOKEN_NOT_FOUND_CODE) {
    return VISITOR_POINT_CLAIM_TOKEN_NOT_FOUND_ERROR_MESSAGE;
  }

  if (errorCode === VISITOR_POINTS_CLAIM_TOKEN_UNAVAILABLE_CODE) {
    return `現在pt付与コードは発行されていません。${settings.supportContactLabel}にお問い合わせください。`;
  }

  if (errorCode === VISITOR_POINTS_CLAIM_TOKEN_EXPIRED_CODE) {
    return VISITOR_POINT_CLAIM_TOKEN_EXPIRED_ERROR_MESSAGE;
  }

  if (errorCode === VISITOR_POINTS_CLAIM_TOKEN_EXHAUSTED_CODE) {
    return VISITOR_POINT_CLAIM_TOKEN_EXHAUSTED_ERROR_MESSAGE;
  }

  if (errorCode === VISITOR_POINTS_CLAIM_TOKEN_INACTIVE_CODE) {
    return VISITOR_POINT_CLAIM_TOKEN_INACTIVE_ERROR_MESSAGE;
  }

  if (errorCode === VISITOR_POINTS_CLAIM_LIMIT_EXCEEDED_CODE) {
    return VISITOR_POINT_CLAIM_LIMIT_EXCEEDED_ERROR_MESSAGE;
  }

  if (errorCode === VISITOR_POINTS_CLAIM_RULE_NOT_FOUND_CODE) {
    return `pt付与ルールが未設定です。${settings.supportContactLabel}にお問い合わせください。`;
  }

  if (errorCode === VISITOR_POINTS_CLAIM_EVENT_UNAVAILABLE_CODE) {
    return VISITOR_POINT_CLAIM_EVENT_UNAVAILABLE_ERROR_MESSAGE;
  }

  if (errorCode === VISITOR_POINTS_CLAIM_RATE_LIMITED_CODE) {
    return VISITOR_POINT_CLAIM_RATE_LIMITED_ERROR_MESSAGE;
  }

  return VISITOR_POINT_CLAIM_FAILED_ERROR_MESSAGE;
};

/** カメラの連続読み取りを抑止する時間 */
const CAMERA_SCAN_LOCK_DURATION_MS = 1500;

/** QR読み取り状態の表示種別 */
const SCAN_FEEDBACK_TYPES = Object.freeze({
  processing: 'processing',
  success: 'success',
  error: 'error',
});

/** QR読み取り直後の処理中表示 */
const SCAN_PROCESSING_FEEDBACK = Object.freeze({
  type: SCAN_FEEDBACK_TYPES.processing,
  eyebrow: 'QR READ',
  title: 'QRコードを読み取りました',
  body: 'このままpt取得まで自動で進みます。画面を閉じずにお待ちください。',
});

/** さきほど取得に成功したQRを読み直したときの表示。成功の続きとして案内し、取得済みエラーにしない。 */
const SCAN_ALREADY_CLAIMED_FEEDBACK = Object.freeze({
  type: SCAN_FEEDBACK_TYPES.success,
  eyebrow: 'ALREADY GOT',
  title: 'このQRのptは受け取り済みです',
  body: 'さきほどこのQRでptを受け取りました。別のQRを読み取るときは「続けて読み取る」を押して、カメラを別のQRへ向けてください。',
});

/**
 * QR読み取り結果パネルの表示クラスを返す
 * @param {'processing' | 'success' | 'error'} feedbackType - 結果表示種別
 * @returns {{container: string, eyebrowContainer: string, eyebrowText: string, title: string, accent: string}} 表示クラス
 */
const buildScanFeedbackClassNames = (feedbackType) => {
  if (feedbackType === SCAN_FEEDBACK_TYPES.success) {
    return {
      container: 'border-app-quest-gold bg-[#FFF4C2]',
      eyebrowContainer: 'bg-app-quest-gold',
      eyebrowText: 'text-app-quest-night',
      title: 'text-app-quest-night',
      accent: 'bg-app-quest-gold',
    };
  }

  if (feedbackType === SCAN_FEEDBACK_TYPES.error) {
    return {
      container: 'border-app-danger bg-app-danger-container',
      eyebrowContainer: 'bg-app-danger',
      eyebrowText: 'text-white',
      title: 'text-[#7B1A1A]',
      accent: 'bg-app-danger',
    };
  }

  return {
    container: 'border-app-primary bg-[#EAF6FF]',
    eyebrowContainer: 'bg-app-primary',
    eyebrowText: 'text-white',
    title: 'text-app-quest-night',
    accent: 'bg-app-primary',
  };
};

/**
 * QR読み取り結果パネル
 * @param {Object} props - プロパティ
 * @param {{type: 'processing' | 'success' | 'error', eyebrow: string, title: string, body: string, eventTitle?: string, pointsGranted?: number}} props.feedback - 表示内容
 * @returns {JSX.Element} QR読み取り結果パネル
 */
const ScanFeedbackPanel = ({ feedback }) => {
  /** 表示種別に対応したクラス */
  const feedbackClassNames = buildScanFeedbackClassNames(feedback.type);

  return (
    <View
      accessibilityLiveRegion={feedback.type === SCAN_FEEDBACK_TYPES.error ? 'assertive' : undefined}
      accessibilityRole={feedback.type === SCAN_FEEDBACK_TYPES.error ? 'alert' : undefined}
      className={`border-4 p-4 shadow-[4px_4px_0_rgba(26,45,77,0.32)] ${feedbackClassNames.container}`}
      id={feedback.type === SCAN_FEEDBACK_TYPES.error ? SCAN_INPUT_ERROR_ID : undefined}
      testID="scan-feedback-panel"
    >
      {feedback.type === SCAN_FEEDBACK_TYPES.success ? (
        <View className="items-center py-2" testID="point-claim-celebration">
          <Text className="text-sm font-extrabold tracking-[3px] text-app-quest-night">✦ POINT GET ✦</Text>
          <Text className="mt-2 text-3xl font-extrabold text-app-quest-night">{feedback.title}</Text>
          <View className="mt-3 w-full border-t-2 border-app-quest-gold/70 pt-2">
            {feedback.eventTitle ? (
              <Text className="truncate text-center text-xs font-semibold text-app-quest-night/80">
                企画: {feedback.eventTitle}
              </Text>
            ) : null}
            <Text className="mt-1 text-center text-sm font-extrabold text-app-quest-night">
              獲得: {feedback.pointsGranted}pt
            </Text>
          </View>
        </View>
      ) : (
        <>
          <View className="flex-row items-center gap-3">
            <View className={`px-3 py-1 ${feedbackClassNames.eyebrowContainer}`}>
              <Text className={`text-[11px] font-extrabold tracking-[1px] ${feedbackClassNames.eyebrowText}`}>
                {feedback.eyebrow}
              </Text>
            </View>
            {feedback.type === SCAN_FEEDBACK_TYPES.processing ? (
              <View className="flex-row gap-1">
                <View className={`h-2 w-2 ${feedbackClassNames.accent}`} />
                <View className={`h-2 w-2 ${feedbackClassNames.accent} opacity-70`} />
                <View className={`h-2 w-2 ${feedbackClassNames.accent} opacity-40`} />
              </View>
            ) : null}
          </View>
          <Text className={`mt-3 text-xl font-extrabold ${feedbackClassNames.title}`}>{feedback.title}</Text>
          <Text className="mt-2 text-sm leading-5 text-app-text">{feedback.body}</Text>
        </>
      )}
    </View>
  );
};

/**
 * ポイント取得成功時の企画名と獲得ptを作る
 * @param {{pointsGranted: number, currentPoint: number, eventTitle?: string}} claimResult - ポイント取得結果
 * @returns {{eventTitle: string, pointsGranted: number, summary: string}} 表示内容
 */
const buildPointClaimSuccessDetails = (claimResult) => {
  if (claimResult?.distributionQrId) {
    return { eventTitle: '', pointsGranted: claimResult.pointsGranted, summary: `配布ポイント / 獲得: ${claimResult.pointsGranted}pt` };
  }
  /** 企画名 */
  const eventTitle = String(claimResult?.eventTitle ?? '企画').trim() || '企画';

  /** 獲得pt */
  const pointsGranted = Number(claimResult?.pointsGranted ?? 0);

  return {
    eventTitle,
    pointsGranted,
    summary: `企画: ${eventTitle} / 獲得: ${pointsGranted}pt`,
  };
};

/**
 * ルートパラメータからポイント取得トークンを抽出する
 * @param {Object | undefined} params - ルートパラメータ
 * @returns {string} ポイント取得トークン
 */
const extractRouteClaimToken = (params) => {
  /** URLから受け取る候補値 */
  const tokenCandidate = params?.token ?? params?.claimToken ?? params?.code ?? params?.t ?? '';

  return extractPointClaimToken(tokenCandidate);
};

/**
 * 来場者QRスキャン画面
 * @param {Object} props - プロパティ
 * @param {Object} props.navigation - ナビゲーション
 * @param {Object} props.route - ルート
 * @returns {JSX.Element} 来場者QRスキャン画面
 */
const VisitorScanScreen = ({ navigation, route }) => {
  /** 屋台ランキング導線から開いたか（旧URLとホームの戻り位置を維持） */
  const isStallRatingEntry =
    route?.params?.scanMode === 'stall-rating';
  /** 形式が共通の8文字コードは、手入力時だけ用途を選択する */
  const [manualCodeType, setManualCodeType] = useState<'point' | 'stall-rating'>(
    isStallRatingEntry ? 'stall-rating' : 'point',
  );
  /** 屋台評価コードを手入力するか */
  const isStallRatingManualInput = manualCodeType === 'stall-rating';
  /** 公開アプリ設定 */
  const { settings } = usePublicAppSettings();

  /** URL状態またはlocalStorageからセッションを復元 */
  const { sessionToken, isSessionReady, handleSessionLost } = useVisitorSession(navigation, route);

  /** ネットワーク接続状態 */
  const { isOnline } = useNetworkStatus();

  /** 入力中のポイント付与コード */
  const [claimToken, setClaimToken] = useState('');

  /** ポイント付与コードの手入力要素 */
  const claimTokenInputRef = useRef<HTMLInputElement | null>(null);

  /** カメラ権限 */
  const [cameraPermission, requestCameraPermission] = useCameraPermissions();

  /** 画面フォーカス状態（フォーカスを失った瞬間にカメラを即時停止するため） */
  const isFocused = useIsFocused();

  /** カメラ表示状態 */
  const [isCameraVisible, setIsCameraVisible] = useState(false);

  /** 連続読み取り防止状態 */
  const [isScanLocked, setIsScanLocked] = useState(false);

  /** 連続読み取り防止の同期ロック（stateは再描画まで反映されず同一フレーム内の連続コールバックを防げないため） */
  const scanLockRef = useRef(false);

  /** 処理中状態 */
  const [isSubmitting, setIsSubmitting] = useState(false);

  /** ポイント取得RPC実行中の同期ロック（二重送信で後着の取得済みエラーが成功表示を上書きするのを防ぐ） */
  const claimInFlightRef = useRef(false);

  /** エラーメッセージ */
  const [errorMessage, setErrorMessage] = useState('');

  /** 成功メッセージ */
  const [successMessage, setSuccessMessage] = useState('');

  /** トースト表示メッセージ */
  const [toastNotice, setToastNotice] = useState(null);

  /** 匿名回答のptコード受取操作。屋台のアクセストークンは不要。 */
  const {
    handleClaimRewardCode, isClaimingReward, rewardClaimError, rewardClaimResult, isClaimDisabled,
  } = useStallRatingRewardClaim({
    sessionToken,
    onSessionLost: handleSessionLost,
    onSuccess: (result) => setToastNotice({
      kind: 'claim',
      title: `${result.pointsGranted} pt を受け取りました！`,
      body: `${result.stallName} / 所持pt: ${result.currentPoint}`,
    }),
  });

  /** QR読み取り結果パネル */
  const [scanFeedback, setScanFeedback] = useState(null);

  /** 現在のコード入力元 */
  const [claimTokenSource, setClaimTokenSource] = useState('manual');

  /** URL経由のポイント取得トークン */
  const routeClaimToken = extractRouteClaimToken(route?.params);

  /**
   * 不正なポイント付与コード入力へ描画後にfocusを移す
   * @returns {void}
   */
  const focusClaimTokenInput = useCallback(() => {
    window.requestAnimationFrame(() => {
      claimTokenInputRef.current?.focus();
    });
  }, []);

  /**
   * コード入力変更時の処理
   * @param {string} value - 入力値
   * @returns {void}
   */
  const handleChangeClaimToken = useCallback((value) => {
    setClaimToken(value);
    setClaimTokenSource('manual');
    setErrorMessage('');
    setSuccessMessage('');
    setScanFeedback(null);
  }, []);

  /** 手入力の用途を切り替え、別用途の入力と結果を消去する */
  const handleChangeManualCodeType = (nextType: 'point' | 'stall-rating') => {
    setManualCodeType(nextType);
    handleChangeClaimToken('');
  };

  /**
   * ポイント取得処理を実行する
   * @param {string} token - 取得対象コード
   * @param {'manual' | 'camera'} source - コード入力元
   * @param {{ preserveToast?: boolean }} [options] - 表示制御オプション
   * @returns {Promise<void>}
   */
  const handleClaimPoint = useCallback(async (
    token,
    source,
    options: { preserveToast?: boolean } = {},
  ) => {
    /** 前後の空白を除去したコード */
    const normalizedToken = extractPointClaimToken(token);
    /** アンケート照合へ流してはいけない企画用8文字コードか */
    const isManualPointClaimCode = isPointClaimManualCode(normalizedToken);

    /** 読み取り直後トーストを維持するかどうか */
    const { preserveToast = false } = options;

    if (!sessionToken) {
      setErrorMessage(VISITOR_POINT_CLAIM_SESSION_ERROR_MESSAGE);
      return;
    }

    if (!normalizedToken) {
      setErrorMessage(VISITOR_POINT_CLAIM_INVALID_INPUT_ERROR_MESSAGE);
      focusClaimTokenInput();
      return;
    }

    if (!isOnline) {
      setErrorMessage(VISITOR_OFFLINE_SUBMIT_BLOCKED_MESSAGE);
      setScanFeedback(null);
      return;
    }

    if (claimInFlightRef.current) {
      return;
    }

    claimInFlightRef.current = true;
    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    if (!preserveToast) {
      setToastNotice(null);
    }

    if (source === 'camera') {
      setScanFeedback(SCAN_PROCESSING_FEEDBACK);
    } else {
      setScanFeedback(null);
    }

    try {
      /** ポイント取得結果 */
      let claimResult = null;

      try {
        /** QRトークンでのポイント取得結果 */
        const qrResult = await visitorPointsClaim(sessionToken, normalizedToken);

        // 配布QRはポイントだけを付与し、企画の訪問や島スタンプには数えない。
        if (qrResult.eventId) {
          // RPC(visitor_get_event_visits) が未導入でもスタンプ帳が更新されるよう、端末キャッシュへ押印する
          try {
            await upsertLocalEventVisit(qrResult.visitorId, qrResult.eventId, new Date().toISOString());
          } catch (stampError) {
            logError('スタンプ帳キャッシュ更新に失敗しました。', {
              errorMessage: stampError instanceof Error ? stampError.message : String(stampError),
              eventId: qrResult.eventId,
              visitorId: qrResult.visitorId,
            });
          }

          try {
            await queuePendingStampCelebration(sessionToken, qrResult.eventId, new Date().toISOString());
          } catch (celebrationError) {
            logError('スタンプ獲得演出データの保存に失敗しました。', {
              errorMessage: celebrationError instanceof Error ? celebrationError.message : String(celebrationError),
              eventId: qrResult.eventId,
              visitorId: qrResult.visitorId,
            });
          }
        }
        claimResult = qrResult;
      } catch (qrError) {
        // QRトークン照合で未検出の場合だけ、既存アンケートコードとして再試行する。
        // 企画用8文字コードは同じ文字列をアンケート照合へ渡さない。
        if (
          qrError instanceof AppError
          && qrError.code === VISITOR_POINTS_CLAIM_TOKEN_NOT_FOUND_CODE
          && !isManualPointClaimCode
        ) {
          /** アンケートコードでのポイント取得結果 */
          const surveyResult = await visitorSurveyClaimPoints(sessionToken, normalizedToken);
          claimResult = surveyResult;
        } else {
          throw qrError;
        }
      }

      if (source === 'camera') {
        /** 成功時の詳細表示 */
        const claimSuccessDetails = buildPointClaimSuccessDetails(claimResult);

        setScanFeedback({
          type: SCAN_FEEDBACK_TYPES.success,
          eyebrow: 'POINT GET',
          title: VISITOR_SCAN_CAMERA_POINT_CLAIM_SUCCESS_TITLE,
          body: claimSuccessDetails.summary,
          eventTitle: claimSuccessDetails.eventTitle,
          pointsGranted: claimSuccessDetails.pointsGranted,
        });
        setSuccessMessage('');
        setToastNotice({
          kind: 'claim',
          title: VISITOR_SCAN_CAMERA_POINT_CLAIM_SUCCESS_TITLE,
          body: claimSuccessDetails.summary,
        });
      } else {
        setSuccessMessage(buildPointClaimSuccessDetails(claimResult).summary);
      }

      playVisitorSound(VISITOR_SOUND_NAMES.pointClaim);
      rememberClaimedPointToken(sessionToken, normalizedToken);

      setClaimToken('');
      setClaimTokenSource('manual');
    } catch (error) {
      if (
        error instanceof AppError
        && (error.code === VISITOR_POINTS_CLAIM_SESSION_INVALID_CODE
          || error.code === VISITOR_SURVEY_CLAIM_SESSION_INVALID_CODE)
      ) {
        await handleSessionLost();
        return;
      }

      logError('ポイント取得に失敗しました。', {
        errorCode: error instanceof AppError ? error.code : '',
        errorMessage: error instanceof Error ? error.message : String(error),
      });

      /** エラーメッセージ */
      let mappedMessage = VISITOR_POINT_CLAIM_FAILED_ERROR_MESSAGE;

      if (error instanceof AppError) {
        if (error.code.startsWith('POINT_DISTRIBUTION_')) {
          mappedMessage = error.message;
        } else if (error.code === VISITOR_SURVEY_CLAIM_ALREADY_CLAIMED_CODE) {
          mappedMessage = VISITOR_SURVEY_CLAIM_ALREADY_CLAIMED_ERROR_MESSAGE;
        } else if (error.code === VISITOR_SURVEY_CLAIM_NOT_FOUND_CODE) {
          mappedMessage = VISITOR_POINT_CLAIM_TOKEN_NOT_FOUND_ERROR_MESSAGE;
        } else if (error.code === VISITOR_SURVEY_CLAIM_RATE_LIMITED_CODE) {
          mappedMessage = VISITOR_SURVEY_CLAIM_RATE_LIMITED_ERROR_MESSAGE;
        } else if (
          error.code === VISITOR_SURVEY_CLAIM_FAILED_CODE
        ) {
          mappedMessage = VISITOR_SURVEY_CLAIM_FAILED_ERROR_MESSAGE;
        } else {
          mappedMessage = mapPointClaimErrorMessage(error.code, settings);
        }
      }

      if (source === 'camera') {
        setToastNotice(null);
        setClaimTokenSource('manual');
        setScanFeedback({
          type: SCAN_FEEDBACK_TYPES.error,
          eyebrow: 'SCAN ERROR',
          title: 'pt取得に失敗しました',
          body: mappedMessage,
        });
      } else {
        setScanFeedback(null);
      }

      setErrorMessage(mappedMessage);
    } finally {
      claimInFlightRef.current = false;
      setIsSubmitting(false);
    }
  }, [
    focusClaimTokenInput,
    handleSessionLost,
    isOnline,
    sessionToken,
    settings,
  ]);

  /** URL経由ポイント取得の自動実行済みフラグ */
  const routeClaimTokenHandledRef = useRef('');

  useEffect(() => {
    if (!isSessionReady || !sessionToken || !routeClaimToken) {
      return;
    }

    if (routeClaimTokenHandledRef.current === routeClaimToken) {
      return;
    }

    routeClaimTokenHandledRef.current = routeClaimToken;

    // 取得後に戻る操作などで同じURLを開き直した場合は、再送せず受け取り済みと案内する。
    if (hasClaimedPointToken(sessionToken, routeClaimToken)) {
      setScanFeedback(SCAN_ALREADY_CLAIMED_FEEDBACK);
      return;
    }

    setClaimToken(routeClaimToken);
    setClaimTokenSource('camera');
    setToastNotice({
      kind: 'scan',
      title: VISITOR_SCAN_CAMERA_SCANNED_MESSAGE,
      body: VISITOR_SCAN_CAMERA_SCANNED_GUIDE_MESSAGE,
    });
    setScanFeedback(SCAN_PROCESSING_FEEDBACK);
    void handleClaimPoint(routeClaimToken, 'camera', { preserveToast: true });
  }, [handleClaimPoint, isSessionReady, routeClaimToken, sessionToken]);

  /**
   * カメラ起動を要求する
   * @returns {Promise<void>}
   */
  const handlePressEnableCamera = useCallback(async () => {
    // Web環境ではQrScanner内で権限を処理するため、直接表示する
    if (Platform.OS === 'web') {
      setIsCameraVisible(true);
      setErrorMessage('');
      return;
    }

    if (cameraPermission?.granted) {
      setIsCameraVisible(true);
      setErrorMessage('');
      return;
    }

    /** 権限要求結果 */
    const permissionResult = await requestCameraPermission();

    if (permissionResult?.granted) {
      setIsCameraVisible(true);
      setErrorMessage('');
      return;
    }

    setErrorMessage(VISITOR_SCAN_CAMERA_PERMISSION_REQUIRED_MESSAGE);
  }, [cameraPermission?.granted, requestCameraPermission]);

  /**
   * 続けて読み取るために表示をクリアしてカメラを再起動する
   * pt獲得成功後にもう一度QRを読み取れるよう、成功表示・入力値・トーストを消してから
   * カメラを一度非表示にし、handlePressEnableCameraで再度起動する（再マウントで確実に再初期化する）。
   * @returns {Promise<void>}
   */
  const handleContinueScanning = useCallback(async () => {
    setScanFeedback(null);
    setSuccessMessage('');
    setErrorMessage('');
    setClaimToken('');
    setClaimTokenSource('manual');
    setToastNotice(null);
    setIsCameraVisible(false);
    await handlePressEnableCamera();
  }, [handlePressEnableCamera]);

  /**
   * スタンプ帳を確認するためホームへ遷移する
   * @returns {void}
   */
  const handleGoToStampBook = useCallback(() => {
    navigation.navigate(VISITOR_ROUTES.HOME);
  }, [navigation]);

  /**
   * カメラを停止して手入力欄へ進む
   * @returns {void}
   */
  const handleUseManualInput = useCallback(() => {
    setIsCameraVisible(false);
    window.requestAnimationFrame(() => {
      document
        .querySelector<HTMLElement>('[data-testid="claim-token-input"]')
        ?.focus();
    });
  }, []);

  /**
   * カメラ読み取り結果を入力欄へ反映する
   * @param {{ data?: string }} scanResult - 読み取り結果
   * @returns {void}
   */
  const handleBarcodeScanned = useCallback((scanResult) => {
    if (
      scanLockRef.current
      || claimInFlightRef.current
      || isScanLocked
      || isSubmitting
      || isClaimingReward
    ) {
      return;
    }

    /** QRの生読み取り値 */
    const scannedValue = String(scanResult?.data ?? '').trim();
    /** 屋台ランキングQRのアクセストークン */
    const stallRatingAccessToken = extractStallRatingAccessToken(scannedValue);

    if (stallRatingAccessToken) {
      // 生トークンをログへ残さず、対象画面だけへ引き渡す。
      logInfo('屋台ランキングQRコードを読み取りました。');
      scanLockRef.current = true;
      setIsScanLocked(true);
      setIsCameraVisible(false);
      navigation.navigate(VISITOR_ROUTES.STALL_RATING, {
        accessToken: stallRatingAccessToken,
      });

      setTimeout(() => {
        scanLockRef.current = false;
        setIsScanLocked(false);
      }, CAMERA_SCAN_LOCK_DURATION_MS);
      return;
    }

    /** ポイント取得コード */
    const scannedToken = extractPointClaimToken(scannedValue);

    // ポイント取得トークンもログへ含めない。
    logInfo('ポイント取得QRコードを読み取りました。');

    if (!scannedToken) {
      return;
    }

    // 企画QRとアンケートは1アカウント1回のため、成功直後の読み直しはRPCを送らず成功の続きとして案内する。
    if (
      !isRepeatablePointClaimToken(scannedToken)
      && hasClaimedPointToken(sessionToken, scannedToken)
    ) {
      scanLockRef.current = true;
      setIsScanLocked(true);
      setIsCameraVisible(false);
      setClaimToken('');
      setClaimTokenSource('manual');
      setErrorMessage('');
      setSuccessMessage('');
      setToastNotice(null);
      setScanFeedback(SCAN_ALREADY_CLAIMED_FEEDBACK);

      setTimeout(() => {
        scanLockRef.current = false;
        setIsScanLocked(false);
      }, CAMERA_SCAN_LOCK_DURATION_MS);
      return;
    }

    setClaimToken(scannedToken);
    setClaimTokenSource('camera');
    setSuccessMessage('');
    setToastNotice({
      kind: 'scan',
      title: VISITOR_SCAN_CAMERA_SCANNED_MESSAGE,
      body: VISITOR_SCAN_CAMERA_SCANNED_GUIDE_MESSAGE,
    });
    setScanFeedback(SCAN_PROCESSING_FEEDBACK);
    setErrorMessage('');
    scanLockRef.current = true;
    setIsScanLocked(true);
    setIsCameraVisible(false);

    void handleClaimPoint(scannedToken, 'camera', { preserveToast: true });

    setTimeout(() => {
      scanLockRef.current = false;
      setIsScanLocked(false);
    }, CAMERA_SCAN_LOCK_DURATION_MS);
  }, [
    handleClaimPoint,
    isClaimingReward,
    isScanLocked,
    isSubmitting,
    navigation,
    sessionToken,
  ]);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
      return undefined;
    }

    /**
     * Playwright / MCP から読み取り完了を再現する
     * @param {Event} event - カスタムイベント
     * @returns {void}
     */
    const handleMockBarcodeScanned = (event) => {
      /** カスタムイベント詳細 */
      const detail = event?.detail ?? {};

      if (typeof detail.token !== 'string' || detail.token.trim() === '') {
        return;
      }

      handleBarcodeScanned({ data: detail.token.trim() });
    };

    // MCP の画面検証時に、実カメラを使わず同じ読み取り完了処理を呼べるようにする
    window.addEventListener('ikomasai:visitor-scan-read', handleMockBarcodeScanned);

    return () => {
      window.removeEventListener('ikomasai:visitor-scan-read', handleMockBarcodeScanned);
    };
  }, [handleBarcodeScanned]);

  /** カメラ起動ハンドラの最新参照（useFocusEffect の再登録を防ぐためのref） */
  const handlePressEnableCameraRef = useRef(handlePressEnableCamera);
  handlePressEnableCameraRef.current = handlePressEnableCamera;

  /** 使い方ガイドの実行中か。ガイドがカメラ欄を指している間にカメラの許可ダイアログを出さない */
  const isGuidedTourRunning = useIsGuidedTourRunning();

  useFocusEffect(
    useCallback(() => {
      // ガイドが終わるとこのeffectが再実行され、通常どおり起動する（QRスキャン画面でスキップした場合も含む）
      if (routeClaimToken || isGuidedTourRunning) {
        return;
      }

      // 画面フォーカス時にカメラを起動する。停止は isFocused による条件レンダーで即時に行うため、
      // ここでは isCameraVisible を維持して再フォーカス時の再マウントを最速化する。
      void handlePressEnableCameraRef.current();
    }, [routeClaimToken, isGuidedTourRunning]),
  );

  /**
   * ポイント取得実行
   * @returns {Promise<void>}
   */
  const handlePressClaimPoint = useCallback(async () => {
    /** URLを貼り付けた場合も、手入力の用途に依存せずQRの種類を判別する */
    const stallQrToken = extractStallRatingAccessToken(claimToken);
    if (stallQrToken || (isStallRatingManualInput && !isPointClaimQrPayload(claimToken))) {
      const stallRatingAccessToken =
        stallQrToken || extractStallRatingAccessTokenFromInput(claimToken);

      if (!stallRatingAccessToken) {
        setErrorMessage(
          '有効な屋台評価用コードではありません。会場のQRを読み取るか、案内された8文字コードを入力してください。',
        );
        focusClaimTokenInput();
        return;
      }

      setIsCameraVisible(false);
      navigation.navigate(VISITOR_ROUTES.STALL_RATING, {
        accessToken: stallRatingAccessToken,
      });
      return;
    }

    await handleClaimPoint(claimToken, claimTokenSource);
  }, [
    claimToken,
    claimTokenSource,
    focusClaimTokenInput,
    handleClaimPoint,
    isStallRatingManualInput,
    navigation,
  ]);

  /** ポイント取得成功表示が出ているか */
  const hasClaimSuccess = Boolean(successMessage) || scanFeedback?.type === SCAN_FEEDBACK_TYPES.success;

  return (
    <ScreenLayout
      badge="pt獲得"
      bottomBar={(
        <VisitorBottomSwitchBar
          activeRouteName={VISITOR_ROUTES.SCAN}
          navigation={navigation}
        />
      )}
      description="企画・屋台のQRをここで読み取れます。企画はpt取得、屋台は評価画面へ自動で進みます。"
      headerLeftAction={(
        <VisitorHomeBackButton
          onPress={() => {
            if (
              isStallRatingEntry &&
              typeof navigation.goBack === 'function'
            ) {
              navigation.goBack();
              return;
            }
            navigation.navigate(VISITOR_ROUTES.HOME);
          }}
        />
      )}
      mascotMood={hasClaimSuccess ? 'happy' : 'focus'}
      mascotPose={hasClaimSuccess ? 'win' : 'guide'}
      theme="visitor"
      title="QRスキャン"
    >
      <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
        <View className="gap-4 pb-8">
          <View className="rounded-none border-2 border-app-border bg-[#FFF8DD] p-4" {...tourAnchor('scan.camera')}>
            <Text className="text-sm font-medium text-app-text">
              {VISITOR_SCAN_CAMERA_SECTION_TITLE}
            </Text>
            <Text className="mt-1 text-xs leading-5 text-app-text-secondary">
              企画・屋台のQRをそのまま読み取ってください。企画はpt取得、屋台は評価画面へ自動で進みます。
            </Text>
            {isCameraVisible && isFocused ? (
              <View className="mt-3 overflow-hidden rounded-none border-2 border-app-border">
                <QrScanner
                  onManualInput={handleUseManualInput}
                  onBarcodeScanned={isScanLocked || isClaimingReward ? undefined : handleBarcodeScanned}
                  purposeLabel="企画・屋台のQR"
                  style={{ height: 280, width: '100%' }}
                />
              </View>
            ) : (
              <View className="mt-3">
                <AppButton
                  label={VISITOR_SCAN_CAMERA_ENABLE_BUTTON_LABEL}
                  onPress={handlePressEnableCamera}
                  variant="secondary"
                />
              </View>
            )}

            {Platform.OS !== 'web' && !cameraPermission?.granted && cameraPermission && !cameraPermission.canAskAgain ? (
              <Text className="mt-2 text-sm font-medium text-app-danger">
                {VISITOR_SCAN_CAMERA_PERMISSION_REQUIRED_MESSAGE}
              </Text>
            ) : null}
          </View>

          {claimTokenSource === 'camera' && claimToken.trim() && isSubmitting ? (
            <ScanFeedbackPanel feedback={scanFeedback ?? SCAN_PROCESSING_FEEDBACK} />
          ) : null}

          {scanFeedback && !isSubmitting ? (
            <ScanFeedbackPanel feedback={scanFeedback} />
          ) : null}

          {successMessage ? (
            <View
              className="rounded-none border-2 border-app-quest-gold bg-amber-50 p-3"
              testID="manual-claim-success-panel"
            >
              <Text className="text-sm font-semibold text-app-quest-night">pt獲得！</Text>
              <Text className="mt-1 text-sm font-medium text-app-quest-night">{successMessage}</Text>
            </View>
          ) : null}

          {hasClaimSuccess ? (
            <View className="flex-row flex-wrap gap-2">
              <View className="min-w-0 flex-1">
                <AppButton
                  label="続けて読み取る"
                  onPress={() => { void handleContinueScanning(); }}
                  variant="secondary"
                />
              </View>
              <View className="min-w-0 flex-1">
                <AppButton
                  label="スタンプ帳を見る"
                  onPress={handleGoToStampBook}
                />
              </View>
            </View>
          ) : null}

          <View>
            <fieldset className="mb-3" disabled={isSubmitting || isClaimingReward}>
              <legend className="mb-1 text-sm font-medium text-app-text">手入力するコードの種類</legend>
              <div className="flex flex-wrap gap-2">
                <label className="flex min-h-11 cursor-pointer items-center gap-2 border-2 border-app-border bg-[#FFF8DD] px-3 py-2 text-sm text-app-text">
                  <input
                    type="radio"
                    name="manual-code-type"
                    value="point"
                    checked={!isStallRatingManualInput}
                    onChange={() => handleChangeManualCodeType('point')}
                  />
                  ポイントコード
                </label>
                <label className="flex min-h-11 cursor-pointer items-center gap-2 border-2 border-app-border bg-[#FFF8DD] px-3 py-2 text-sm text-app-text">
                  <input
                    type="radio"
                    name="manual-code-type"
                    value="stall-rating"
                    checked={isStallRatingManualInput}
                    onChange={() => handleChangeManualCodeType('stall-rating')}
                  />
                  屋台評価コード
                </label>
              </div>
            </fieldset>
            <Text className="mb-1 text-sm font-medium text-app-text">
              {isStallRatingManualInput
                ? '屋台評価コード（手入力）'
                : 'ポイントコード（手入力）'}
            </Text>
            <Text className="mb-2 text-xs text-app-text-secondary">
              {isStallRatingManualInput
                ? 'QRが読み取れない場合は、QRと一緒に表示された8文字コードを入力してください。'
                : 'QRが読み取れない場合は、QRと一緒に表示されたコードを入力してください。企画・配布の8文字コード、発行済みのPD-で始まるコード、アンケートコードを利用できます。'}
            </Text>
            <TextInput
              accessibilityLabel={
                isStallRatingManualInput
                  ? '屋台評価コード（手入力）'
                  : 'ポイントコード（手入力）'
              }
              aria-describedby={
                scanFeedback?.type === SCAN_FEEDBACK_TYPES.error || errorMessage
                  ? SCAN_INPUT_ERROR_ID
                  : undefined
              }
              aria-invalid={scanFeedback?.type === SCAN_FEEDBACK_TYPES.error || Boolean(errorMessage)}
              autoCapitalize="characters"
              autoCorrect={false}
              className="rounded-none border-2 border-app-border bg-[#FFFDF0] px-4 py-3 text-base text-app-text"
              onChangeText={handleChangeClaimToken}
              placeholder="例: 2A3B4C5D"
              placeholderTextColor={INPUT_PLACEHOLDER_COLOR}
              ref={claimTokenInputRef}
              testID="claim-token-input"
              value={claimToken}
              {...tourAnchor('scan.manualCode')}
            />
          </View>

          <AppButton
            disabled={isSubmitting || isClaimingReward || !isOnline}
            label={
              isStallRatingManualInput
                ? '屋台評価へ進む'
                : isSubmitting
                  ? POINT_CLAIM_BUTTON_LOADING_LABEL
                  : POINT_CLAIM_BUTTON_LABEL
            }
            onPress={handlePressClaimPoint}
          />

          {errorMessage && scanFeedback?.type !== SCAN_FEEDBACK_TYPES.error ? (
            <View
              accessibilityLiveRegion="assertive"
              accessibilityRole="alert"
              className="rounded-none border-2 border-app-danger bg-red-50 p-3"
              id={SCAN_INPUT_ERROR_ID}
            >
              <Text className="text-sm font-medium text-app-danger">{errorMessage}</Text>
            </View>
          ) : null}
          <StallRatingRewardClaimPanel
            claimResult={rewardClaimResult}
            errorMessage={rewardClaimError}
            isClaiming={isClaimingReward}
            isDisabled={isClaimDisabled || isSubmitting}
            onClaim={handleClaimRewardCode}
          />
        </View>
      </ScrollView>

      <NoticeToast notice={toastNotice} onDismiss={() => setToastNotice(null)} />
    </ScreenLayout>
  );
};

export default VisitorScanScreen;
